import { useGetLists } from '@vlabs/api/artemy/client';
import type { GetCardsFilterKeys, PointGetCardsFilters } from '@vlabs/api/types';
import { FastFilter } from '@vlabs/uikit';

import { createFastFilterDateLabel } from '@vlabs/monokit/shared/lib/fast-filter-date-label';
import { FastFilterGroup } from '@vlabs/monokit/shared/ui/fast-filter-group';
import { withPopover } from '@vlabs/monokit/shared/ui/popovers/withPopover';

import {
  CardIdFilterWithBottomPopover,
  DateFilterWithBottomPopover,
  FioFilterWithBottomPopover,
} from './fast-filters';
import { hasIntersection } from './hasIntersections';
import { ListsFilterPopover } from './popovers/ListsFilterPopoverPoint3';
import { useCardsFilters } from './useCardsFilters';
import { FIO_FILTER_KEY } from './utils';

const withBottomPopover = withPopover({ placement: 'bottom-start' });
const withFastBottomPopover = withBottomPopover(FastFilter);
const ListsFilterWithBottomPopover = withFastBottomPopover(ListsFilterPopover);

interface CardListFastFiltersProps {
  exclude?: (keyof PointGetCardsFilters)[];
}

function CardListFastFiltersPoint3({
  exclude = undefined,
}: CardListFastFiltersProps): JSX.Element {
  const { filters, updateFilters } = useCardsFilters();
  const getLists = useGetLists({ list_ids: filters.list_ids }, {
    query: {
      enabled: (filters?.list_ids?.length ?? 0) > 0,
    },
  });
  const listOptions = getLists.data?.lists?.map((list) => ({ label: list.user_data, value: list.list_id })) ?? [];

  const isExcluded = (filterKeys: GetCardsFilterKeys) => hasIntersection<GetCardsFilterKeys>(exclude, filterKeys);

  const getListsName = (arr: string[]): string[] => {
    if (!arr?.length || !listOptions?.length) return [];

    return arr.map((id) => {
      const option = listOptions.find((list) => list.value === id);
      return option?.label ?? id;
    });
  };

  return (
    <FastFilterGroup>
      {Object.entries(filters).map(([name, value]) => {
        if (value === undefined) return undefined;
        if (isExcluded([name] as GetCardsFilterKeys)) return undefined;
        switch (name) {
          case 'card_ids': return (
            <CardIdFilterWithBottomPopover
              data-testid={name}
              key={name}
              onClear={() => updateFilters({ card_ids: undefined })}
              value={value}
            />
          );
          case FIO_FILTER_KEY: return (
            <FioFilterWithBottomPopover
              data-testid={name}
              key={name}
              onClear={() => updateFilters({ [FIO_FILTER_KEY]: undefined })}
              value={value}
            />
          );
          case 'list_ids': return (
            <ListsFilterWithBottomPopover
              data-testid={name}
              key={name}
              onClear={() => updateFilters({ list_ids: undefined })}
              value={getListsName(value)}
            />
          );
          default: return undefined;
        }
      })}
      {!!(filters.create_time__gte ?? filters.create_time__lt) && (
        <DateFilterWithBottomPopover
          data-testid="create_time"
          key="create_time"
          onClear={() => updateFilters({ create_time__gte: undefined, create_time__lt: undefined })}
          value={createFastFilterDateLabel({
            createTimeGte: filters.create_time__gte,
            createTimeLt: filters.create_time__lt,
          })}
        />
      )}
    </FastFilterGroup>
  );
}

export { CardListFastFiltersPoint3 };
